import React, { useEffect, useState, useCallback } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import i18n from '@ohif/i18n';
import { SidePanel, type SidePanelVariant } from '@ohif/ui-next';
import { Types } from '@ohif/core';

function formatLeftPanelStudySummary(displaySets: unknown[]): string {
  const list = displaySets ?? [];
  const seriesCount = list.length;
  const imageCount = list.reduce((sum, ds) => {
    const d = ds as {
      numImages?: number;
      numImageFrames?: number;
      instances?: unknown[];
    };
    const n =
      typeof d.numImages === 'number'
        ? d.numImages
        : typeof d.numImageFrames === 'number'
          ? d.numImageFrames
          : Array.isArray(d.instances)
            ? d.instances.length
            : 1;
    return sum + Math.max(0, n);
  }, 0);
  return i18n.t('SeriesImagesSummary', {
    ns: 'PatientView',
    seriesCount,
    imageCount,
  });
}

export type SidePanelWithServicesProps = {
  servicesManager: AppTypes.ServicesManager;
  side: 'left' | 'right';
  className?: string;
  panelVariant?: SidePanelVariant;
  activeTabIndex: number;
  tabs?: any;
  expandedWidth?: number;
  onClose: () => void;
  onOpen: () => void;
  isExpanded: boolean;
  collapsedWidth?: number;
  expandedInsideBorderSize?: number;
  collapsedInsideBorderSize?: number;
  collapsedOutsideBorderSize?: number;
};

const SidePanelWithServices = ({
  servicesManager,
  side,
  activeTabIndex: activeTabIndexProp,
  isExpanded,
  tabs: tabsProp,
  onOpen,
  onClose,
  panelVariant = 'light',
  className,
  ...props
}: SidePanelWithServicesProps) => {
  const { t } = useTranslation('PatientView');
  const panelService = servicesManager?.services?.panelService;

  // Tracks whether this SidePanel has been opened at least once since this SidePanel was inserted into the DOM.
  // Thus going to the Study List page and back to the viewer resets this flag for a SidePanel.
  const [sidePanelExpanded, setSidePanelExpanded] = useState(isExpanded);
  const [activeTabIndex, setActiveTabIndex] = useState(activeTabIndexProp ?? 0);
  const [closedManually, setClosedManually] = useState(false);
  const [allTabs, setAllTabs] = useState(tabsProp ?? panelService.getPanels(side));
  const [leftHeaderSummary, setLeftHeaderSummary] = useState('');

  const REPORT_PANEL_ID = '@ohif/extension-radiology-report.panelModule.reportPanel';

  // Check if report panel is currently active based on the actual active tab
  const isReportPanelActive =
    activeTabIndex < allTabs.length && allTabs[activeTabIndex]?.id === REPORT_PANEL_ID;

  // Always filter out report panel from visible tabs, unless it's currently active
  // When active, we need to include it temporarily so it can be displayed
  const tabs = (isReportPanelActive
    ? allTabs // Include report panel when it's active
    : allTabs.filter(tab => tab.id !== REPORT_PANEL_ID)
  ).map(tab =>
    tab.id === REPORT_PANEL_ID
      ? { ...tab, label: t('Report'), iconLabel: t('Report') }
      : tab
  );

  const handleActiveTabIndexChange = useCallback(({ activeTabIndex }) => {
    setActiveTabIndex(activeTabIndex);
  }, []);

  const handleOpen = useCallback(() => {
    setSidePanelExpanded(true);
    onOpen?.();
  }, [onOpen]);

  const handleClose = useCallback(() => {
    setSidePanelExpanded(false);
    setClosedManually(true);
    // If report panel was active when closing, switch to first non-report panel
    if (activeTabIndex < allTabs.length && allTabs[activeTabIndex]?.id === REPORT_PANEL_ID) {
      const firstNonReportIndex = allTabs.findIndex(tab => tab.id !== REPORT_PANEL_ID);
      if (firstNonReportIndex !== -1) {
        setActiveTabIndex(firstNonReportIndex);
      }
    }
    onClose?.();
  }, [onClose, activeTabIndex, allTabs]);

  useEffect(() => {
    setSidePanelExpanded(isExpanded);
  }, [isExpanded]);

  useEffect(() => {
    if (side !== 'left') {
      return;
    }
    const displaySetService = servicesManager?.services?.displaySetService;
    if (!displaySetService) {
      return;
    }
    const updateSummary = () => {
      setLeftHeaderSummary(formatLeftPanelStudySummary(displaySetService.getActiveDisplaySets()));
    };
    updateSummary();
    const subChanged = displaySetService.subscribe(
      displaySetService.EVENTS.DISPLAY_SETS_CHANGED,
      updateSummary
    );
    const subAdded = displaySetService.subscribe(
      displaySetService.EVENTS.DISPLAY_SETS_ADDED,
      updateSummary
    );
    return () => {
      subChanged.unsubscribe();
      subAdded.unsubscribe();
    };
  }, [side, servicesManager, i18n.language]);

  /** update the active tab index from outside */
  useEffect(() => {
    setActiveTabIndex(activeTabIndexProp ?? 0);
  }, [activeTabIndexProp]);

  useEffect(() => {
    const { unsubscribe } = panelService.subscribe(
      panelService.EVENTS.PANELS_CHANGED,
      panelChangedEvent => {
        if (panelChangedEvent.position !== side) {
          return;
        }

        setAllTabs(panelService.getPanels(side));
      }
    );

    return () => {
      unsubscribe();
    };
  }, [panelService, side]);

  useEffect(() => {
    const activatePanelSubscription = panelService.subscribe(
      panelService.EVENTS.ACTIVATE_PANEL,
      (activatePanelEvent: Types.ActivatePanelEvent) => {
        if (sidePanelExpanded || activatePanelEvent.forceActive) {
          // Find panel in all tabs (including hidden report panel)
          const allTabsIndex = allTabs.findIndex(tab => tab.id === activatePanelEvent.panelId);
          if (allTabsIndex !== -1) {
            // If forceActive is true, always open the panel and reset closedManually flag
            if (activatePanelEvent.forceActive) {
              setClosedManually(false);
              setSidePanelExpanded(true);
              setActiveTabIndex(allTabsIndex);
            } else if (!closedManually) {
              setSidePanelExpanded(true);
              // For non-report panels, calculate visible index
              if (activatePanelEvent.panelId === REPORT_PANEL_ID) {
                setActiveTabIndex(allTabsIndex);
              } else {
                // Count how many non-report panels are before this index
                const visibleIndex = allTabs
                  .slice(0, allTabsIndex)
                  .filter(tab => tab.id !== REPORT_PANEL_ID).length;
                setActiveTabIndex(visibleIndex);
              }
            }
          }
        }
      }
    );

    return () => {
      activatePanelSubscription.unsubscribe();
    };
  }, [allTabs, sidePanelExpanded, panelService, closedManually]);

  // Calculate the correct activeTabIndex for the filtered tabs
  const getVisibleTabIndex = (allTabsIndex: number): number => {
    if (allTabsIndex < 0 || allTabsIndex >= allTabs.length) {
      return 0;
    }
    // If pointing to report panel, it shouldn't be in filtered tabs, so return 0 or first valid tab
    if (allTabs[allTabsIndex]?.id === REPORT_PANEL_ID) {
      // Report panel is not in filtered tabs, return index of first non-report panel
      return 0;
    }
    // Count non-report panels before this index
    return allTabs.slice(0, allTabsIndex).filter(tab => tab.id !== REPORT_PANEL_ID).length;
  };

  const visibleActiveTabIndex = isReportPanelActive
    ? activeTabIndex
    : getVisibleTabIndex(activeTabIndex);

  return (
    <SidePanel
      {...props}
      className={classnames(panelVariant === 'light' && 'viewer-chrome-light', className)}
      variant={panelVariant}
      side={side}
      leftHeaderSummary={side === 'left' ? leftHeaderSummary : undefined}
      tabs={tabs}
      activeTabIndex={visibleActiveTabIndex}
      isExpanded={sidePanelExpanded}
      onOpen={handleOpen}
      onClose={handleClose}
      onActiveTabIndexChange={({ activeTabIndex: newIndex }) => {
        // When user clicks a tab, map it back to allTabs index
        if (isReportPanelActive) {
          // If report panel was active, check if user is switching away from it
          const clickedTab = tabs[newIndex];
          if (clickedTab?.id === REPORT_PANEL_ID) {
            // User clicked on report panel, keep it active
            setActiveTabIndex(allTabs.findIndex(tab => tab.id === REPORT_PANEL_ID));
          } else {
            // User is switching away from report panel, map to correct index in allTabs
            const allTabsIndex = allTabs.findIndex(tab => tab.id === clickedTab?.id);
            if (allTabsIndex !== -1) {
              setActiveTabIndex(allTabsIndex);
            }
          }
        } else {
          // Report panel is not active, find the corresponding index in allTabs
          const clickedTab = tabs[newIndex];
          const allTabsIndex = allTabs.findIndex(tab => tab.id === clickedTab?.id);
          if (allTabsIndex !== -1) {
            setActiveTabIndex(allTabsIndex);
          }
        }
        handleActiveTabIndexChange({ activeTabIndex: newIndex });
      }}
    />
  );
};

export default SidePanelWithServices;
